/**
 * ============================================
 * SONY/DOMO COUNTRY FILTER
 * ============================================
 *
 * The backend ships `byCountry`: every series on the tab per ISO-2 country,
 * fetched with the same filters as its all-countries twin (verified: the sum
 * over all countries equals the total for every day/month). For a selection we
 * rebuild the payload's series from the selected countries only, in the exact
 * shapes the dashboard already reads, so every card follows the filter.
 * Sony keeps one row per country per period, so summed unique accounts stay
 * exact across a set of countries.
 */

import type { PS5AnalyticsData as PS5Data } from '@/hooks/usePS5Analytics';
import { countryName, type CountryOption } from '@/utils/countryRegions';

type Series = Record<string, Record<string, number[]>>;

export interface DomoByCountry {
  countries: Record<string, string>;
  cols: Record<string, string[]>;
  daily: Series;      // [dau, sessions, hours]
  monthly: Series;    // [mau, sessions, hours]
  crash: Series;      // [sessions, crashes]
  retention: Series;  // [acquired, retained, reclaimed, lapsed, acquiredLTD]
  installs: Series;   // [total, first, re]
  downloads: Series;  // [downloads]
  lifetime: Record<string, number[]>; // [accounts, sessions, hours]
}

const r1 = (x: number) => Math.round(x * 10) / 10;
const r2 = (x: number) => Math.round(x * 100) / 100;

/** "2026-01-01" -> "Jan-26" (DOMO's Month label format). */
function monthLabel(ms: string): string {
  const [y, m] = ms.split('-').map(Number);
  const d = new Date(y, (m || 1) - 1, 1);
  return `${d.toLocaleDateString('en-US', { month: 'short' })}-${String(y).slice(2)}`;
}

/** Sum the selected countries' value arrays per period. */
function sumSeries(series: Series | undefined, codes: string[], width: number): Array<[string, number[]]> {
  const out: Array<[string, number[]]> = [];
  for (const [period, byCode] of Object.entries(series || {})) {
    const acc = new Array(width).fill(0);
    let any = false;
    for (const cc of codes) {
      const v = byCode[cc];
      if (!v) continue;
      any = true;
      for (let i = 0; i < width; i++) acc[i] += v[i] || 0;
    }
    if (any) out.push([period, acc]);
  }
  return out.sort((a, b) => a[0].localeCompare(b[0]));
}

export function domoCountryOptions(bc: DomoByCountry | undefined): CountryOption[] {
  if (!bc) return [];
  return Object.entries(bc.countries).map(([code, name]) => ({
    code, name: name || countryName(code), count: bc.lifetime[code]?.[0] || 0,
  }));
}

/**
 * The payload as if only `codes` existed. The daily/crash series come back
 * newest-first, like the backend's.
 */
export function filterDomoData(data: PS5Data, codes: string[]): PS5Data {
  const bc = (data as any).byCountry as DomoByCountry | undefined;
  if (!bc || !codes.length) return data;

  const daily = sumSeries(bc.daily, codes, 3);
  const dailyStats = daily.map(([Date, [dau, sessions, hours]]) => ({
    Date, DAU: dau, Devices: 0, TotalSessions: sessions, TotalHours: r2(hours),
    AvgSessionMinutes: sessions ? r1((hours * 60) / sessions) : 0,
  })).reverse();

  const monthlyStats = sumSeries(bc.monthly, codes, 3).map(([ms, [mau, sessions, hours]]) => ({
    Month: monthLabel(ms), MonthStartDate: ms, MAU: mau, NewUsers: 0, ReturningUsers: 0,
    TotalSessions: sessions, TotalHours: hours, AvgSessionMinutes: sessions ? r1((hours * 60) / sessions) : 0,
  })).reverse();

  const crashDaily = sumSeries(bc.crash, codes, 2).map(([Date, [sessions, crashes]]) => ({
    Date, TotalSessions: sessions, TotalCrashes: crashes, DevicesWithCrashes: 0, TotalDevices: 0,
    CrashRate: sessions ? r2((crashes / sessions) * 100) : 0,
  })).reverse();

  const retentionData = sumSeries(bc.retention, codes, 5).map(([ms, [acquired, retained, reclaimed, lapsed, ltd]]) => ({
    month: monthLabel(ms), monthStart: ms, acquired, retained, reclaimed, lapsed,
    active: acquired + retained + reclaimed, acquiredLTD: ltd, lapsedLTD: 0,
  }));

  const installsByMonth = sumSeries(bc.installs, codes, 3).map(([ms, [total, first, re]]) => ({
    monthStart: ms, month: monthLabel(ms), value: total, firstInstalls: first, reinstalls: re,
  }));
  const downloadsByMonth = sumSeries(bc.downloads, codes, 1).map(([ms, [n]]) => ({
    monthStart: ms, month: monthLabel(ms), value: n,
  }));

  // Lifetime geo cards: keep only the selected countries, recompute shares.
  const names = new Set(codes.map(c => bc.countries[c]).filter(Boolean));
  const geoRows = (data.geoBreakdown || []).filter((g: any) => names.has(g.Country));
  const geoTotal = geoRows.reduce((s: number, g: any) => s + (g.Users || 0), 0);
  const geoBreakdown = geoRows.map((g: any) => ({ ...g, Percentage: geoTotal ? r1((g.Users / geoTotal) * 100) : 0 }));

  // Title-Level daily-by-country panels: drop the other countries.
  const geoDaily = (data as any).geoDaily;
  let geoDailyF = geoDaily;
  if (geoDaily?.countries && geoDaily?.days) {
    const keep = new Set<number>();
    geoDaily.countries.forEach(([name]: [string, string], i: number) => { if (names.has(name)) keep.add(i); });
    geoDailyF = {
      ...geoDaily,
      days: Object.fromEntries(Object.entries(geoDaily.days as Record<string, number[][]>)
        .map(([d, rows]) => [d, rows.filter(r => keep.has(r[0]))])),
    };
  }

  const lifetime = codes.reduce((s, c) => s + (bc.lifetime[c]?.[0] || 0), 0);
  const lastRet = retentionData[retentionData.length - 1];
  const lastCrash = crashDaily[0];
  const hours = dailyStats.reduce((s, d) => s + d.TotalHours, 0);
  const sessions = dailyStats.reduce((s, d) => s + d.TotalSessions, 0);

  return {
    ...data,
    summary: {
      ...data.summary,
      totalDownloads: downloadsByMonth.reduce((s, m) => s + m.value, 0),
      totalInstalls: installsByMonth.reduce((s, m) => s + m.value, 0),
      allTimeUsers: lifetime,
      totalUniqueUsers: lifetime,
      totalUsers: lifetime,
      currentEngaged: lastRet?.active || 0,
      currentEngagedMonth: lastRet?.month || '',
      currentCrashRate: lastCrash?.CrashRate || 0,
      currentCrashDate: lastCrash?.Date || '',
      latestDAU: dailyStats[0]?.DAU || 0,
      currentMAU: monthlyStats[0]?.MAU || 0,
      totalSessions: sessions,
      totalPlaytimeHours: r1(hours),
      totalHours: r1(hours),
      avgSessionMinutes: sessions ? r1((hours * 60) / sessions) : 0,
      totalCountries: codes.length,
      countriesReached: codes.length,
    } as PS5Data['summary'],
    dailyStats: dailyStats as PS5Data['dailyStats'],
    monthlyStats: monthlyStats as PS5Data['monthlyStats'],
    crashStats: { ...(data.crashStats || {}), daily: crashDaily } as PS5Data['crashStats'],
    retentionData: retentionData as PS5Data['retentionData'],
    geoBreakdown: geoBreakdown as PS5Data['geoBreakdown'],
    ...({ installsByMonth, downloadsByMonth, geoDaily: geoDailyF } as any),
  };
}

/**
 * The top `n` countries (among `codes`, or all) by monthly active players summed
 * over the months in range. One ranking for the whole tab so a country keeps the
 * same colour on every graph.
 */
export function topDomoCountries(
  bc: DomoByCountry | undefined, codes: string[], monthInRange: (ms: string) => boolean, n = 3,
): string[] {
  if (!bc) return [];
  const pool = codes.length ? codes : Object.keys(bc.countries);
  const score: Record<string, number> = {};
  for (const [ms, byCode] of Object.entries(bc.monthly)) {
    if (!monthInRange(ms)) continue;
    for (const cc of pool) score[cc] = (score[cc] || 0) + (byCode[cc]?.[0] || 0);
  }
  return Object.entries(score).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).slice(0, n).map(([c]) => c);
}

/**
 * Add one line per top country to an already-aggregated chart: each country's
 * single-country payload goes through the SAME aggregator, then its value is
 * joined onto the total's rows by x label as `top0`, `top1`, `top2`.
 */
export function addCountryLines<T extends { date: string }>(
  base: T[], perCountry: Array<Array<{ date: string } & Record<string, unknown>>>, valueKey: string,
): Array<T & Record<string, number | undefined>> {
  const maps = perCountry.map(rows => new Map(rows.map(r => [r.date, r[valueKey] as number])));
  return base.map(row => {
    const extra: Record<string, number | undefined> = {};
    maps.forEach((m, i) => { extra[`top${i}`] = m.get(row.date); });
    return { ...row, ...extra };
  });
}
